import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Button } from '../../../../../design-system/button/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../../../../design-system/form/form-primitives';
import { Inline } from '../../../../../design-system/inline/inline';
import { Input } from '../../../../../design-system/input/input';
import { Modal, ModalContent, ModalTitle } from '../../../../../design-system/modal/modal';
import { Stack } from '../../../../../design-system/stack/stack';
import { cn } from '../../../../../utils/cn';

type PsuIdCollectionModalProps = {
  isOpen: boolean;
  onClose: () => void;
  institutionName: string;
  institutionLogo: string;
  onSubmit: (psuId: string) => void;
};

// Validation schema with either/or logic
const psuIdFormSchema = z
  .object({
    bankId: z
      .string()
      .optional()
      .refine((val) => !val || /^[a-zA-Z0-9]*$/.test(val), {
        message: 'Bank ID must be alphanumeric',
      })
      .refine((val) => !val || val.length <= 40, {
        message: 'Bank ID must be 40 characters or less',
      }),
    branchNumber: z
      .string()
      .optional()
      .refine((val) => !val || /^\d+$/.test(val), {
        message: 'Must contain only digits',
      })
      .refine((val) => !val || val.length === 3, {
        message: 'Must be exactly 3 digits',
      }),
    accountNumber: z
      .string()
      .optional()
      .refine((val) => !val || /^\d+$/.test(val), {
        message: 'Must contain only digits',
      })
      .refine((val) => !val || val.length === 7, {
        message: 'Must be exactly 7 digits',
      }),
  })
  .refine(
    (data) => {
      // Either bankId OR (branchNumber AND accountNumber) must be provided
      const hasBankId = !!data.bankId && data.bankId.length > 0;
      const hasBranchAndAccount =
        !!data.branchNumber &&
        data.branchNumber.length === 3 &&
        !!data.accountNumber &&
        data.accountNumber.length === 7;
      return hasBankId || hasBranchAndAccount;
    },
    {
      message: 'Please provide either a Bank ID or both Branch Number and Account Number',
      path: ['bankId'], // Show error on bankId field
    },
  );

type PsuIdFormValues = z.infer<typeof psuIdFormSchema>;

/**
 * Modal to collect PSU-ID (Payment Service User Identifier) for institutions that require it.
 *
 * Deutsche Bank and Noris Bank require PSU-ID which can be provided as:
 * - Option 1: Bank ID (alphanumeric, up to 40 characters)
 * - Option 2: Branch Number (3 digits) + Account Number (7 digits)
 */
export const PsuIdCollectionModal = ({
  isOpen,
  onClose,
  institutionName,
  institutionLogo,
  onSubmit,
}: PsuIdCollectionModalProps) => {
  const form = useForm<PsuIdFormValues>({
    resolver: zodResolver(psuIdFormSchema),
    defaultValues: {
      bankId: '',
      branchNumber: '',
      accountNumber: '',
    },
    mode: 'onChange',
  });

  const handleSubmit = (data: PsuIdFormValues) => {
    // Determine which option was used and construct PSU-ID
    const psuId = data.bankId || `${data.branchNumber}${data.accountNumber}`;
    onSubmit(psuId);

    // Reset form
    form.reset();
  };

  const handleCancel = () => {
    form.reset();
    onClose();
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      handleCancel();
    }
  };

  return (
    <Modal open={isOpen} onOpenChange={handleOpenChange}>
      <ModalContent className="h-auto max-h-[90vh] max-w-sm overflow-y-auto">
        {/* Header */}
        <div className="flex justify-start gap-3 p-5">
          <img src={institutionLogo} alt="" loading="eager" fetchPriority="high" className="h-12 w-12 rounded-full" />

          <div className="flex flex-col justify-start gap-1">
            <ModalTitle className="text-sm font-medium leading-5 text-black">Enter Your Banking Details</ModalTitle>
            <p className="text-xs text-seapoint-gray">{institutionName}</p>
          </div>
        </div>

        {/* Content */}
        <div className="px-5 pb-5">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)} id="psu-id-form">
              <Stack space={3}>
                {/* Bank ID */}
                <FormField
                  control={form.control}
                  name="bankId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-gray-900">{institutionName} ID</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="text"
                          placeholder="Enter your bank ID"
                          maxLength={40}
                          className={cn(
                            'rounded-lg border-seapoint-gray-border',
                            form.formState.errors.bankId && 'border-red-500',
                          )}
                          onChange={(e) => {
                            // Only allow alphanumeric characters
                            const cleaned = e.target.value.replace(/[^a-zA-Z0-9]/g, '');
                            field.onChange(cleaned);
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* OR Divider */}
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="bg-white px-2 text-gray-500">OR</span>
                  </div>
                </div>

                {/* Branch + Account */}
                <div className="grid grid-cols-2 gap-3">
                  {/* Branch Number */}
                  <FormField
                    control={form.control}
                    name="branchNumber"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium text-gray-900">Branch</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            type="text"
                            inputMode="numeric"
                            placeholder="123"
                            maxLength={3}
                            className={cn(
                              'rounded-lg border-seapoint-gray-border',
                              form.formState.errors.branchNumber && 'border-red-500',
                            )}
                            onChange={(e) => {
                              // Only allow digits
                              const cleaned = e.target.value.replace(/\D/g, '').slice(0, 3);
                              field.onChange(cleaned);
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Account Number */}
                  <FormField
                    control={form.control}
                    name="accountNumber"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium text-gray-900">Account</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            type="text"
                            inputMode="numeric"
                            placeholder="4567890"
                            maxLength={7}
                            className={cn(
                              'rounded-lg border-seapoint-gray-border',
                              form.formState.errors.accountNumber && 'border-red-500',
                            )}
                            onChange={(e) => {
                              // Only allow digits
                              const cleaned = e.target.value.replace(/\D/g, '').slice(0, 7);
                              field.onChange(cleaned);
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Help text */}
                <p className="text-xs text-gray-600">
                  You can find these details on your bank statement or online banking portal.
                </p>
              </Stack>
            </form>
          </Form>
        </div>

        {/* Actions */}
        <Inline space={2} className="bg-seapoint-gray-medium p-5">
          <Button type="submit" form="psu-id-form" disabled={!form.formState.isValid} className="flex-1">
            Continue
          </Button>
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
        </Inline>
      </ModalContent>
    </Modal>
  );
};
